import { Check, Eye, EyeOff, KeyRound, Lock } from "lucide-react";
import { useState, type ReactNode } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";

import { IconButton } from "@/components/ui/button";
import { Field, InputGroup } from "@/components/ui/field";
import type { TranslationKey } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/stores/locale";

/**
 * Mirrors `Password` and `validate_password_strength` on the backend, so someone
 * learns their password is too weak while they are typing it rather than after
 * a round trip. The API still enforces it — this is convenience, not the check.
 *
 * All four classes are required, so each one is its own line: "three of four"
 * is a rule nobody can check at a glance about their own password.
 */
export const MIN_LENGTH = 8;
export const MAX_LENGTH = 20;
const CLASSES: [TranslationKey, RegExp][] = [
  ["pw.ruleLower", /[a-z]/],
  ["pw.ruleUpper", /[A-Z]/],
  ["pw.ruleDigit", /\d/],
  ["pw.ruleSymbol", /[^A-Za-z0-9]/],
];

/** Every rule the API will apply, in the order the checklist shows them. */
export const passwordRules = (value: string) => [
  {
    key: "pw.ruleLength" as const,
    met: value.length >= MIN_LENGTH && value.length <= MAX_LENGTH,
  },
  ...CLASSES.map(([key, pattern]) => ({ key, met: pattern.test(value) })),
];

/** Whether a value clears all of them — the same answer the API gives. */
export const passwordValid = (value: string) => passwordRules(value).every((rule) => rule.met);

/** A password input with its own reveal toggle, styled as the legacy reset screen. */
export function PasswordField({
  id,
  label,
  error,
  register,
  ...props
}: {
  id: string;
  label: string;
  error?: string | undefined;
  register: UseFormRegisterReturn;
} & Omit<React.ComponentProps<"input">, "id">) {
  const t = useTranslation();
  const [revealed, setRevealed] = useState(false);

  return (
    <Field label={label} htmlFor={id} error={error}>
      <InputGroup
        id={id}
        icon={Lock}
        fieldSize="lg"
        type={revealed ? "text" : "password"}
        aria-invalid={Boolean(error)}
        trailing={
          <IconButton
            label={revealed ? t("login.hidePassword") : t("login.showPassword")}
            size="icon-sm"
            onClick={() => setRevealed((shown) => !shown)}
            aria-pressed={revealed}
          >
            {revealed ? <Eye aria-hidden /> : <EyeOff aria-hidden />}
          </IconButton>
        }
        {...props}
        {...register}
      />
    </Field>
  );
}

/**
 * The checklist is the validation, shown as it is met rather than after.
 *
 * Six full-width rows of "not met yet" is a wall of failure to someone who has
 * not typed anything yet, so the rules are chips that flow instead: the same
 * words, a third of the height, and a count that says how far along you are
 * without reading them all.
 */
export function Requirements({ rules }: { rules: { key: TranslationKey; met: boolean }[] }) {
  const t = useTranslation();
  const met = rules.filter((rule) => rule.met).length;

  return (
    <div className="mt-1 grid gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-fg-muted text-subhead font-medium">{t("pw.requirements")}</p>
        {/* The count is the live region, not the list: six chips flipping as
            someone types would be read out on every keystroke. */}
        <p
          aria-live="polite"
          className={cn(
            "text-footnote tabular-nums transition-colors",
            met === rules.length ? "text-success font-medium" : "text-fg-subtle",
          )}
        >
          {met}/{rules.length}
        </p>
      </div>

      {/* A rule that is met changes its colour, its mark and its border, and
          says so to a screen reader — nothing here rests on green alone. */}
      <ul className="flex flex-wrap gap-1.5">
        {rules.map(({ key, met }) => (
          <li
            key={key}
            className={cn(
              "text-footnote inline-flex items-center gap-1.5 rounded-full border py-1 pr-2.5 pl-2",
              "transition-colors duration-[var(--duration-fast)]",
              met
                ? "border-success/30 bg-success-soft text-success"
                : "border-line text-fg-subtle",
            )}
          >
            {met ? (
              <Check className="size-3" strokeWidth={3} aria-hidden />
            ) : (
              <span aria-hidden className="bg-fg-subtle/40 size-1.5 rounded-full" />
            )}
            {t(key)}
            <span className="sr-only">{met ? " — met" : " — not met yet"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Why the screen opened on its own.
 *
 * Both password screens can be arrived at without asking for them — a forced
 * change, an expiry — and a form that appears unbidden and says nothing reads
 * as a fault. Amber, not red: the account is fine, something is simply due.
 */
export function ReasonNotice({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      className="border-warning/25 bg-warning-soft text-callout flex items-start gap-2.5 rounded-xl border px-3.5 py-3"
    >
      <KeyRound className="text-warning mt-0.5 size-4 shrink-0" aria-hidden />
      <span className="text-fg min-w-0 text-pretty">{children}</span>
    </div>
  );
}

/**
 * The account the new password belongs to, for the password manager's benefit.
 *
 * A change-password form carries no username of its own, and without one
 * Chrome cannot tell which account it is generating for — so it offers neither
 * a strong password nor to save the result. Off-screen rather than
 * `display: none`, because a field that is not laid out is a field the browser
 * does not count; read-only and out of the tab order, because nobody is meant
 * to edit it here. A screen reader still reads whose account this is, which is
 * worth knowing before you set a password on it.
 */
export function UsernameForManager({ value }: { value: string }) {
  return (
    <input
      type="text"
      name="username"
      autoComplete="username"
      value={value}
      readOnly
      tabIndex={-1}
      className="sr-only"
    />
  );
}
